import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Line } from "@vds/lines";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { Loader } from "@vds/loaders";
import * as viewCaseDetailsActions from "../../store/actions/cases/viewCaseDetailsActions";
import * as ViewCases from "../../api/cases/index";
import { saveAs } from "file-saver";
import mime from "mime-types";
import AddComment from "./AddComment";
import AddAttachmentTryAgain from "./AddAttachmentTryAgain";
import * as GlobalActions from "../../store/actions/global/global";
import { useTranslation } from "react-i18next";
import { pendoTrack } from "../../util/pendoUtil";
import { TextWrapper, Wrapper, TextWrapperSpan, CPButton, Img, CustomisedTertiaryButton } from "../../constants/common/StyledGlobalConstants";
import CPNotification from "../../pages/Login/CPNotification";
import CPIcon from "../../pages/Login/CPIcon";
import Chip from "./Chip";
import RedesignedModal from "../common/RedesignedModal";
import Ellipse from "../../assets/icons/Ellipse.svg";
import SkeletonLoader from "./SkeletonLoader";
import { useSnackbar } from "react-simple-snackbar";
import RedesignedBreadCrumbs from "../common/RedesignedBreadCrumbs";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import { CustomisedTextLink } from "./AddAttachmentImpl";
import PropTypes from "prop-types";

const { TABLET } = DEVICE_BREAKPOINTS;

const CustomisedLine = styled(Line)`
  height: 1px;
  background: #d8dada;
  margin: ${(props) => (props.margin ? props.margin : "")};
`;

const Text = styled.p`
  font-family: ${(props) => (props.fontFamily ? props.fontFamily : "")};
  margin-top: ${(props) => (props.marginTop ? props.marginTop : "")};
  margin-bottom: ${(props) => (props.marginBottom ? props.marginBottom : "")};
  line-height: ${(props) => (props.lineHeight ? props.lineHeight : "")};
  font-size: ${(props) => {
    switch (props.size) {
      case "small":
        return "10px";
      case "medium":
        return "14px";
      case "large":
        return "18px";
      case "XLarge":
        return "32px";
      default:
        return "12px";
    }
  }};
  ${(props) =>
    props.bold &&
    css`
      font-weight: bold;
    `};
`;

const Spacer = styled.div`
  margin-top: ${(props) => (props.top ? props.top : "")};
  margin-bottom: ${(props) => (props.bottom ? props.bottom : "")};
  margin-left: ${(props) => (props.left ? props.left : "0px")};
  word-break: break-word;
  ${(props) => props.width && "width: " + props.width};
`;

const SnackBarButton = styled(CPButton)`
  & span {
    color: #0096e4;
  }
`;

const StyledH2 = styled.h2`
  font-family: ${(props) => (props.fontFamily ? props.fontFamily : "inherit")};
  font-size: ${(props) => (props.fontSize ? props.fontSize : "1.25rem")};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "700")};
  line-height: ${(props) => (props.lineHeight ? props.lineHeight : "1.5rem")};
  margin: 0;
`;

const StyledH3 = styled.h3`
  font-family: ${(props) => (props.fontFamily ? props.fontFamily : "inherit")};
  font-size: ${(props) => (props.fontSize ? props.fontSize : "0.875rem")};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "700")};
  line-height: ${(props) => (props.lineHeight ? props.lineHeight : "1.25rem")};
  margin: 0;
`;

const ViewCaseDetails = (props) => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [filesToBeUploaded, setFilesToBeUploaded] = useState([]);
  const [createdDate, setCreatedDate] = useState("");
  const [closeCaseModal, setCloseCaseModal] = useState(false);
  const [uniqueActivityList, setUniqueActivityList] = useState([]);
  const [showRequestToCloseCase, setShowRequestToCloseCase] = useState(false);
  const [updatedDate, setUpdatedDate] = useState("");

  const caseNumberRef = useRef(null);
  const breadcrumbRef = useRef(null);
  const firstActivityRef = useRef(null);

  const backToCaseGrid = () => {
    navigate(`/support/cases/caseList`);
  };

  const [t, i18n] = useTranslation("casesTranslation");
  const getMargin = () => {
    if (props.regionId === "840" || props.regionId === "124") {
      if (props.userLanguage === "en-US" && props.windowSize.width < 710) return "4rem";
      else if (props.userLanguage !== "en-US" && props.windowSize.width < 810) return "4rem";
      else return "0";
    } else {
      return "0";
    }
  };

  const BreadcrumbsItemsList = [
    {
      name: t("Support cases"),
      id: "support-case-back-link",
    },
    { name: `${t("Case")} ${props.caseNo}`, id: "case-number" },
  ];
  const [openSnackbar] = useSnackbar({
    position: "bottom-center",
    style: {
      borderRadius: "4px",
      boxShadow: "0px 1px 16px 0px rgba(0,0,0,0.25)",
      backgroundColor: "#000",
      fontFamily: "neue-haas-grotesk-text",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "20px",
      textAlign: "center",
      marginBottom: `${getMargin()}`,
    },
    closeStyle: {
      display: "none",
    },
  });
  const [closeCaseAttempt, setCloseCaseAttempt] = useState(0);

  useEffect(() => {
    if (!props.loader) {
      if (showRequestToCloseCase || props.requestClosure) {
        breadcrumbRef.current?.focus();
      } else {
        caseNumberRef?.current?.focus();
      }
    }
  }, [props.loader]);

  useEffect(() => {
    return () => {
      props.resetCloseCase();
      setShowRequestToCloseCase(false);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (props.userLanguage === "fr-FR") {
      i18n.changeLanguage("frFR");
    } else if (props.userLanguage === "de-DE") {
      i18n.changeLanguage("deDE");
    } else if (props.userLanguage === "nl-NL") {
      i18n.changeLanguage("nlNL");
    } else if (props.userLanguage === "it-IT") {
      i18n.changeLanguage("itIT");
    } else if (props.userLanguage === "pt-PT") {
      i18n.changeLanguage("ptPT");
    } else if (props.userLanguage === "pl-PL") {
      i18n.changeLanguage("plPL");
    } else if (props.userLanguage === "es-ES") {
      i18n.changeLanguage("esES");
    } else {
      i18n.changeLanguage("enUS");
    }
    if (props.caseReplySuccess) {
      props.setCaseReplySuccess(false);
    }
    if (window.location.href.toString().includes("reveal.us.fleetmatics.com") || window.location.href.toString().includes("reveal.us.vzconnect.com")) {
      props.setEnvironment("production");
    }
    setShowRequestToCloseCase(false);
  }, []);

  useEffect(() => {
    document.title = props.title;
  }, [props.title]);

  useEffect(() => {
    if (caseId) {
      props.setCaseID(caseId);
      props.getAllViewCaseDetails();
      props.getActivity();
    }
    return () => props.resetViewCaseDetails();
  }, [caseId]);

  useEffect(() => {
    if (props.createdDate) {
      setCreatedDate(props.createdDate);
      setUpdatedDate(props.activityList[0]?.createdDate);
    }
    let seen = {};
    let uniqueArr =
      props.activityList?.filter((activity) => {
        let key = `${activity.vccpDescription}|${activity.createdDate}`;
        if (!seen[key]) {
          seen[key] = true;
          return true;
        }
        return false;
      }) || [];
    setUniqueActivityList(uniqueArr);
  }, [props.createdDate, props.regionId, props.activityList]);

  useEffect(() => {
    let actualFile = [];
    if (props.attachmentStatusResponse.length !== 0 && props.attachmentName.length !== 0) {
      if (props.attachmentStatusResponse.length === props.attachmentName.length) {
        for (let index = 0; index < props.attachmentName.length; index++) {
          if (props.attachmentStatusResponse[index] === "false") {
            actualFile.push(props.attachmentName[index]);
          }
        }
        if (actualFile.length !== 0) {
          setFilesToBeUploaded([...actualFile]);
          props.setFileTryAgainModal(true);
        }
      }
    }
  }, [props.attachmentStatusResponse]);

  useEffect(() => {
    if (props.closeCaseSuccess) {
      props.getAllViewCaseDetails(props.loader);
      setShowRequestToCloseCase(true);
      props.getActivity();
      props.resetCloseCase();
    }
    if (props.closeCaseError) {
      props.resetCloseCase();
      if (closeCaseAttempt < 2) {
        openSnackbar(
          <Wrapper
            fontFamily="neue-haas-grotesk-text"
            role="alert"
            tabIndex={"0"}
            display="grid"
            gridTemplateColumns="auto auto"
            alignItems="center"
            aria-live="assertive"
          >
            {t("Request to close case failed.")}
            <SnackBarButton
              padding="8px 16px"
              boxButton={true}
              fontSize="12px"
              buttonWidth={"auto"}
              buttonHeight="32px"
              id="try-again-button"
              onClick={handleTryAgain}
              fontPaddingLeft="0"
              fontPaddingRight="0"
              aria-live="assertive"
              tabIndex="0"
            >
              {t("TRY AGAIN")}
            </SnackBarButton>
          </Wrapper>,
          closeCaseAttempt === 1 ? 10000 : 4000
        );
      } else {
        openSnackbar(
          <p role="alert" style={{ margin: "0" }}>
            {t("Something has gone wrong. Try again later.")}
          </p>,
          4000
        );
      }
    }
  }, [props.closeCaseError, props.closeCaseSuccess, closeCaseAttempt]);

  const handleTryAgain = () => {
    let attempt = closeCaseAttempt;
    attempt++;
    setCloseCaseAttempt(attempt);
    props.closeCase();
  };

  const handleDownload = (fileName, documentID) => {
    if (props.viewallotherCasesPermission !== "ReadOnly" || props.viewFinancialCasesPermission !== "ReadOnly") {
      ViewCases.getViewAttachmentCall({ documentId: documentID })
        .then((res) => {
          if (res.data.data) {
            handleDownloadFile(fileName, res.data.data);
            pendoTrack("Customer Portal | Support Cases | View Case Details | Download Attachments", {
              status: "Success",
              statusCode: res.data.statusCode,
              accountId: props.accountNumber,
            });
          }
        })
        .catch((err) => {
          pendoTrack("Customer Portal | Support Cases | View Case Details | Download Attachments", {
            status: "Failed",
            statusCode: err.response.data.statusCode,
            accountId: props.accountNumber,
          });
          console.log("Error", err);
        });
    }
  };

  const handleDownloadFile = (fname, fileData) => {
    let fileExtension = fname.split(".").pop();
    let mimeType = mime.lookup(fileExtension);
    mimeType === undefined || mimeType === null
      ? handleFileCreation(fileData, "application/octet-stream", fname)
      : handleFileCreation(fileData, mimeType, fname);
  };

  const handleFileCreation = (fileData, mimeData, fname) => {
    let bstr = window.atob(fileData);
    let n = bstr.length;
    let uint8Array = new Uint8Array(n);
    while (n--) {
      uint8Array[n] = bstr.charCodeAt(n);
    }
    let file = new File([uint8Array], fname, { type: mimeData });
    saveAs(file);
  };

  const handleCloseCase = () => {
    props.closeCase();
    setCloseCaseModal(false);
  };

  const openedByText = `${t("Opened by")} ${t(props.openedBy)}`;
  const getResponseGridCol1 = () => (props.windowSize.width < TABLET ? "span 3" : "1");
  const getResponseGridCol2 = () => (props.windowSize.width < TABLET ? "span 3" : "2");
  const getResponseGridCol3 = () => (props.windowSize.width < TABLET ? "span 3" : "3");
  const getResponseGridRow = () => (props.windowSize.width < TABLET ? "2" : "1");
  const getResponsiveMarginRight = () => (props.windowSize.width >= 680 ? "1rem" : "0rem");
  const getResponsemarginTop = () => (props.windowSize.width < TABLET ? "1rem" : null);

  return (
    <div>
      {props.filesTryAgainModal && (
        <AddAttachmentTryAgain filesToBeUploaded={filesToBeUploaded} addCommentCaseId={props.caseId} addCommentTryAgain={true} />
      )}
      {props.loader && <Loader active={props.loader} fullscreen={true} />}
      <Spacer>
        <RedesignedBreadCrumbs Items={BreadcrumbsItemsList} handleBack={backToCaseGrid} ref={breadcrumbRef}></RedesignedBreadCrumbs>
      </Spacer>

      {!props.loader && (
        <h1 className="sr-only" tabIndex="-1" ref={caseNumberRef}>
          {t("Case")} {props.caseNo}
        </h1>
      )}
      <Wrapper marginTop="3rem" marginBottom="2rem" tabIndex={-1} aria-hidden={"true"}>
        <TextWrapper fontFamily="neue-haas-grotesk-display" fontSize="2.5rem" fontWeight="700" lineHeight="3rem" aria-hidden="true">
          {t("Case")} {props.caseNo}
        </TextWrapper>
      </Wrapper>
      <CPNotification isOpen={showRequestToCloseCase || props.requestClosure} notificationType="info" height="auto" margin="0rem 0rem 1rem 0rem">
        <TextWrapperSpan fontFamily="neue-haas-grotesk-text" fontSize="0.875rem" lineHeight="1.25rem">
          {t("Case closed.")}
        </TextWrapperSpan>
      </CPNotification>
      <Wrapper display="grid" gridTemplateColumns="50% 25% auto" borderRadius="0.5rem" border="1px solid #D8DADA" padding="1.5rem">
        <Wrapper
          gridColumn={props.windowSize.width < TABLET ? "span 3" : "1"}
          gridRow={getResponseGridRow()}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="16px"
          flexShrink="0"
          marginRight={getResponsiveMarginRight()}
        >
          <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
            <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
              {t("Subject")}
            </StyledH3>
            <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" wordBreak="break-word">
              {props.subject ? t(props.subject) : "-"}
            </TextWrapper>
          </Wrapper>
          <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
            <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
              {t("Details")}
            </StyledH3>
            <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" wordBreak="break-word" whiteSpace="pre-line">
              {props.initialEnquiry ? t(props.initialEnquiry) : "-"}
            </TextWrapper>
          </Wrapper>
          <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
            <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
              {t("Contact")}
            </StyledH3>
            <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem">
              {props.contact ? t(props.contact) : "-"}
            </TextWrapper>
          </Wrapper>
          <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
            <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
              {t("Type")}
            </StyledH3>
            <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" wordBreak="break-word">
              {props.caseType ? t(`${props.caseType}`) : "-"}
            </TextWrapper>
          </Wrapper>
        </Wrapper>
        <Wrapper display="grid" gridColumn={getResponseGridCol2()} gridRow={props.windowSize.width < TABLET ? "3" : "1"}>
          <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="16px" flexShrink="0">
            <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px" marginTop={getResponsemarginTop()}>
              <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
                {t("Date created")}
              </StyledH3>
              <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem">
                {createdDate}
              </TextWrapper>
            </Wrapper>
            {props.status !== "New" && (
              <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
                <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
                  {t("Date updated")}
                </StyledH3>
                <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem">
                  {updatedDate}
                </TextWrapper>
              </Wrapper>
            )}
            {props.status === "Closed" && (
              <Wrapper display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
                <StyledH3 fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem">
                  {t("Date closed")}
                </StyledH3>
                <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem">
                  {props.closedDate}
                </TextWrapper>
              </Wrapper>
            )}
          </Wrapper>
        </Wrapper>
        <Wrapper display="grid" gridColumn={getResponseGridCol3()} gridRow="1">
          <Wrapper height="1.625rem" display="flex" justifyContent="flex-end" alignItems="flex-start" flexShrink="0">
            <Chip status={props.status}>
              {props.status === "Pending Follow up" || props.status === "Holding for customer Reply"
                ? t("Awaiting your reply")
                : t(`${props.status}`)}
            </Chip>
          </Wrapper>
        </Wrapper>
        <Wrapper gridColumn="span 3" gridRow={props.windowSize.width < TABLET ? "4" : "2"}>
          <CustomisedLine margin="23px 0 16px 0"></CustomisedLine>
          <Wrapper display="grid" gridTemplateColumns={props.windowSize.width < TABLET ? "null" : "50% 50%"} gap={getResponsemarginTop()}>
            <Wrapper gridColumn={getResponseGridCol1()} gridRow="1" aria-label={openedByText} tabIndex={-1}>
              <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" color="#747474">
                {t(`Opened by`)}:{" "}
                <TextWrapperSpan fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" color="#000">
                  {t(`${props.openedBy}`)}
                </TextWrapperSpan>
              </TextWrapper>
            </Wrapper>

            {(props.status === "New" || props.status === "Assigned" || props.requestClosure) && (
              <Wrapper gridColumn={getResponseGridCol2()} gridRow={getResponseGridRow()} display="flex" justifyContent="flex-end">
                <CPButton
                  aria-label="Close case"
                  id="close-case-button"
                  padding="12px 16px"
                  buttonWidth={props.windowSize.width < TABLET ? "100%" : "auto"}
                  fontSize="0.875rem"
                  buttonHeight="2.5rem"
                  secondary={true}
                  boxButton={true}
                  onClick={() => setCloseCaseModal(true)}
                  disabled={
                    props.viewallotherCasesPermission === "ReadOnly" ||
                    props.viewFinancialCasesPermission === "ReadOnly" ||
                    showRequestToCloseCase ||
                    props.requestClosure
                  } //disable close case button when user with readonly permission
                >
                  {t(`CLOSE CASE`)}
                </CPButton>
              </Wrapper>
            )}
          </Wrapper>
        </Wrapper>
      </Wrapper>
      <Wrapper display="grid" borderRadius="0.5rem" border="1px solid #D8DADA" marginTop="1.5rem">
        <Wrapper padding="1.5rem" style={{ paddingBottom: "0.5rem" }}>
          <StyledH2 fontSize="1.25rem" fontWeight="700" lineHeight="1.5rem">
            {t("Activity")}
          </StyledH2>
          {props.viewallotherCasesPermission === "ReadOnly" ||
          props.viewFinancialCasesPermission === "ReadOnly" ||
          props.status === "Closed" ? null : (
            <AddComment caseStatus={props.status} requestClosure={props.requestClosure} firstActivityRef={firstActivityRef} />
          )}
        </Wrapper>
        {props.viewallotherCasesPermission === "ReadOnly" || props.viewFinancialCasesPermission === "ReadOnly" || props.status === "Closed" ? null : (
          <CustomisedLine margin="0"></CustomisedLine>
        )}
        <Wrapper width="100%" height="auto">
          {props.commentsLoading ? <SkeletonLoader windowSizeWidth={props.windowSize.width} isLoading={props.commentsLoading} /> : null}
        </Wrapper>

        <Wrapper padding="0.5rem 1.5rem">
          {props.activityError ? (
            <Wrapper paddingBottom="1rem">
              <CPNotification isOpen={true} notificationType="error" height="auto">
                <Wrapper fontFamily="neue-haas-grotesk-text" fontSize="0.875rem">
                  {t("Failed to fetch activity information.")}
                </Wrapper>
              </CPNotification>
            </Wrapper>
          ) : (
            <ActivityLog
              t={t}
              data={uniqueActivityList}
              regionId={props.regionId}
              handleDownload={handleDownload}
              width={props.windowSize.width}
              isReadOnly={props.viewallotherCasesPermission === "ReadOnly" || props.viewFinancialCasesPermission === "ReadOnly"}
              isLoading={props.commentsLoading}
              firstActivityRef={firstActivityRef}
            ></ActivityLog>
          )}
        </Wrapper>
      </Wrapper>

      {closeCaseModal && (
        <RedesignedModal
          opened={closeCaseModal}
          onClickClose={() => setCloseCaseModal(false)}
          title={t("Close case?")}
          isScrollable={false}
          ModalAriaLabel="close case modal"
          button={
            <>
              <CustomisedTertiaryButton
                aria-label="Cancel"
                buttonWidth={"auto"}
                id="modal-cancel-button"
                secondary
                onClick={() => setCloseCaseModal(false)}
              >
                {t("CANCEL")}
              </CustomisedTertiaryButton>
              <CPButton
                aria-label="Close case"
                boxButton={true}
                fontSize="0.875rem"
                buttonWidth={"auto"}
                buttonHeight="2.5rem"
                id="modal-close-case-button"
                onClick={handleCloseCase}
              >
                {t("CLOSE CASE")}
              </CPButton>
            </>
          }
        >
          <Wrapper>
            <TextWrapper margin="0" fontStyle="normal" fontWeight="400" fontSize="14px" style={{ overflow: "hidden", height: "auto" }}>
              {t("Once a case is closed it cannot be reopened.")}{" "}
            </TextWrapper>
          </Wrapper>
        </RedesignedModal>
      )}
    </div>
  );
};

const ActivityLog = ({ t, data, regionId, handleDownload, isReadOnly, width, isLoading, firstActivityRef }) => {
  const activityRef = useRef(null);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        firstActivityRef.current?.focus();
      }, 100);
    } else {
      setTimeout(() => {
        activityRef.current.focus();
      }, 100);
    }
  }, [isLoading]);

  const getButtonWidth = (title) => {
    if (width < 353 && title.length > 38) {
      return "90%";
    } else if (width < 419 && title.length > 39) {
      return "90%";
    } else if (width < 481 && title.length > 46) {
      return "90%";
    } else {
      return null;
    }
  };

  return (
    <>
      {isLoading && (
        <span className="sr-only" tabIndex="-1" ref={activityRef}>
          Please wait while we load the activity log.
        </span>
      )}
      {data.map((item, index) => {
        return (
          <Wrapper
            key={`${index}-${item.vccpFrom}`}
            padding="1rem 0"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="0.5rem"
            alignSelf="stretch"
          >
            <Wrapper display="flex" alignItems="center" gap="0.5rem">
              <TextWrapper fontSize="0.875rem" fontWeight="700" lineHeight="1.25rem" ref={index === 0 ? firstActivityRef : null} tabIndex="-1">
                {t(`${item.vccpFrom}`)}
              </TextWrapper>
              <Img aria-hidden="true" src={Ellipse} aria-label="ellipse" ></Img>
              <TextWrapper color="#747474" fontSize="0.75rem" fontWeight="400" lineHeight="1.125rem">
                {item.createdDate}
              </TextWrapper>
            </Wrapper>
            {item.vccpDescription && (
              <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem">
                {t(`${item.vccpDescription}`)}
              </TextWrapper>
            )}
            <Wrapper display="flex" gap="1rem" flexDirection="column">
              {item.attachments &&
                item.attachments.length !== 0 &&
                item.attachments.map((attachment, index) => (
                  <Wrapper display="flex" gap="1rem" key={`${attachment.documentID}-${index}`}>
                    <CustomisedTextLink
                      id={`attachment-link-${index}`}
                      type="standAlone"
                      size="large"
                      style={{ fontSize: "14px", fontFamily: "neue-haas-grotesk-text" }}
                      width={getButtonWidth(attachment.attachmentTitle)}
                      onClick={() => {
                        handleDownload(attachment.attachmentTitle, attachment.documentID);
                      }}
                    >
                      {attachment.attachmentTitle}
                    </CustomisedTextLink>
                    {isReadOnly ? (
                      <span></span>
                    ) : (
                      <CPIcon
                        id={`download-attachment-${index}`}
                        tabIndex="0"
                        ariaLabel={`Download ${attachment.attachmentTitle}`}
                        clickHandler={() => {
                          handleDownload(attachment.attachmentTitle, attachment.documentID);
                        }}
                      />
                    )}
                  </Wrapper>
                ))}
            </Wrapper>
          </Wrapper>
        );
      })}
    </>
  );
};

const mapDispatchToProps = {
  getAllViewCaseDetails: viewCaseDetailsActions.getAllViewCaseDetails,
  getActivity: viewCaseDetailsActions.getActivity,
  closeCase: viewCaseDetailsActions.closeCase,
  resetViewCaseDetails: viewCaseDetailsActions.resetViewCaseDetails,
  setCommentsModal: viewCaseDetailsActions.setCommentsModal,
  setEnvironment: viewCaseDetailsActions.setEnvironment,
  setCaseReplySuccess: viewCaseDetailsActions.setCaseReplySuccess,
  setAttachmentStatusResponse: viewCaseDetailsActions.setAttachmentStatusResponse,
  setAttachmentName: viewCaseDetailsActions.setAttachmentName,
  setAttachmentToBeUploaded: viewCaseDetailsActions.setAttachmentToBeUploaded,
  setFileTryAgainModal: GlobalActions.setFileTryAgainModal,
  resetCloseCase: viewCaseDetailsActions.resetCloseCase,
  setCaseID: viewCaseDetailsActions.setCaseID,
};

const mapStateToProps = (state) => {
  return {
    viewallotherCasesPermission: state.ACCOUNT.viewallotherCasesPermission,
    viewFinancialCasesPermission: state.ACCOUNT.viewFinancialCasesPermission,
    userLanguage: state.ACCOUNT.userLanguage,
    caseId: state.VIEWCASEDETAILS.caseId,
    caseNo: state.VIEWCASEDETAILS.caseNo,
    subject: state.VIEWCASEDETAILS.subject,
    createdDate: state.VIEWCASEDETAILS.createdDate,
    contact: state.VIEWCASEDETAILS.contact,
    initialEnquiry: state.VIEWCASEDETAILS.initialEnquiry,
    assignedTo: state.VIEWCASEDETAILS.assignedTo,
    caseType: state.VIEWCASEDETAILS.caseType,
    status: state.VIEWCASEDETAILS.status,
    attachments: state.VIEWCASEDETAILS.attachments,
    loader: state.VIEWCASEDETAILS.loading,
    openCommentsModal: state.VIEWCASEDETAILS.openCommentsModal,
    caseReplySuccess: state.VIEWCASEDETAILS.caseReplySuccess,
    activityList: state.VIEWCASEDETAILS.activityList,
    attachmentStatusResponse: state.VIEWCASEDETAILS.attachmentStatusResponse,
    attachmentName: state.VIEWCASEDETAILS.attachmentName,
    filesTryAgainModal: state.ACCOUNT.filesTryAgainModal,
    fileTryAgainSuccess: state.ACCOUNT.fileTryAgainSuccess,
    windowSize: state.ACCOUNT.windowSize,
    regionId: state.ACCOUNT.regionId,
    accountNumber: state.ACCOUNT.accountNumber,
    activityError: state.VIEWCASEDETAILS.activityError,
    closedDate: state.VIEWCASEDETAILS.closedDate,
    openedBy: state.VIEWCASEDETAILS.openedBy,
    commentsLoading: state.VIEWCASEDETAILS.commentsLoading,
    closeCaseSuccess: state.VIEWCASEDETAILS.closeCaseSuccess,
    closeCaseError: state.VIEWCASEDETAILS.closeCaseError,
    requestClosure: state.VIEWCASEDETAILS.requestClosure,
    comments: state.VIEWCASEDETAILS.comments,
    commentAttachmentCheck: state.VIEWCASEDETAILS.commentAttachmentCheck,
  };
};

ActivityLog.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isReadOnly: PropTypes.bool,
  width: PropTypes.number,
  isLoading: PropTypes.bool,
  handleDownload: PropTypes.func,
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firstActivityRef: PropTypes.shape({ current: PropTypes.any }),
};

ViewCaseDetails.propTypes = {
  openedBy: PropTypes.string,
  title: PropTypes.string,
  setCaseID: PropTypes.func,
  getAllViewCaseDetails: PropTypes.func,
  getActivity: PropTypes.func,
  resetViewCaseDetails: PropTypes.func,
  createdDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  closedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activityList: PropTypes.arrayOf(
    PropTypes.shape({
      vccpDescription: PropTypes.string,
      createdDate: PropTypes.string,
    })
  ),
  attachmentStatusResponse: PropTypes.arrayOf(PropTypes.string),
  attachmentName: PropTypes.arrayOf(PropTypes.string),
  setFileTryAgainModal: PropTypes.func,
  closeCaseSuccess: PropTypes.bool,
  closeCaseError: PropTypes.bool,
  caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filesTryAgainModal: PropTypes.bool,
  subject: PropTypes.string,
  initialEnquiry: PropTypes.string,
  userLanguage: PropTypes.string,
  width: PropTypes.number,
  viewallotherCasesPermission: PropTypes.string,
  viewFinancialCasesPermission: PropTypes.string,
  accountNumber: PropTypes.string,
  loader: PropTypes.bool,
  contact: PropTypes.string,
  caseType: PropTypes.string,
  resetCloseCase: PropTypes.func,
  requestClosure: PropTypes.bool,
  caseReplySuccess: PropTypes.bool,
  setCaseReplySuccess: PropTypes.func,
  setEnvironment: PropTypes.func,
  status: PropTypes.string,
  caseNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closeCase: PropTypes.func,
  commentsLoading: PropTypes.bool,
  activityError: PropTypes.bool,
  windowSize: PropTypes.shape({
    width: PropTypes.number,
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewCaseDetails);
